import { Controller } from "@hotwired/stimulus"

// Right-click action popup for a list that is NOT a data table.
//
// The overview popup lives in selection_controller, but every part of it is
// tied to the table: it reads <tr> elements through the row checkboxes,
// intersects the actions of a multi-row selection, and POSTs one endpoint with
// ids[]. A card that renders an <ul> of memberships has none of that, so this
// is the same interaction reduced to its single-row core rather than a reason
// to rebuild the card as a data table.
//
// A row declares its own items:
//   data-action="contextmenu->context-menu#show"
//   data-context-menu-items-value='[{"label":"…","url":"/…","method":"post"}]'
export default class extends Controller {
  static targets = ["popup", "items"]

  connect() {
    this.onDocClick = this.onDocClick.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    document.addEventListener("click", this.onDocClick)
    document.addEventListener("keydown", this.onKeydown)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick)
    document.removeEventListener("keydown", this.onKeydown)
  }

  show(event) {
    if (!this.hasPopupTarget) return
    const items = this.rowItems(event.currentTarget)
    if (items.length === 0) return

    event.preventDefault()
    this.render(items)

    const popup = this.popupTarget
    popup.classList.remove("hidden")
    const margin = 4
    popup.style.left = `${Math.min(event.clientX, window.innerWidth - popup.offsetWidth - margin)}px`
    popup.style.top = `${Math.min(event.clientY, window.innerHeight - popup.offsetHeight - margin)}px`
  }

  hide() {
    if (this.hasPopupTarget) this.popupTarget.classList.add("hidden")
  }

  rowItems(row) {
    try {
      return JSON.parse(row.dataset.contextMenuItemsValue || "[]")
    } catch (_e) {
      return []
    }
  }

  render(items) {
    const container = this.itemsTarget
    container.innerHTML = ""
    for (const item of items) {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
      button.textContent = item.label
      button.dataset.testId = "context-menu-item"
      button.addEventListener("click", (event) => {
        event.stopPropagation()
        this.submit(item)
      })
      container.appendChild(button)
    }
  }

  // A Turbo-handled form so the server redirect reloads the screen, matching
  // the overview popup rather than inventing a second submit path.
  submit(item) {
    this.hide()
    if (!item.url) return

    const form = document.createElement("form")
    form.method = "post"
    form.action = item.url
    form.style.display = "none"

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) form.appendChild(this.hidden("authenticity_token", token))
    if (item.method && item.method !== "post") form.appendChild(this.hidden("_method", item.method))

    document.body.appendChild(form)
    if (form.requestSubmit) form.requestSubmit()
    else form.submit()
  }

  hidden(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    return input
  }

  onDocClick(event) {
    if (this.hasPopupTarget && !this.popupTarget.contains(event.target)) this.hide()
  }

  onKeydown(event) {
    if (event.key === "Escape") this.hide()
  }
}
